import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconShoppingCart,
  IconCategory,
  IconPackage,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { AutoDataTable } from "../../../components/AutoDataTable";
import { CreationForm } from "../../../components/form/CreationForm";
import {
  DetailPageHeader,
  ListPageHeader,
} from "../../../components/PageHeader";
import { StatCard } from "../../../components/StatCard";
import { DetailPageSkeleton } from "../../../components/SkeletonLoader";
import { useConfirmModal } from "../../../components/ConfirmModal";
import { MetadataEditor } from "../components/MetadataEditor";
import { useOrganizationLimits } from "../../../../../business/limits.hook";
import { LimitBadge } from "../../../components/LimitIndicator";
import {
  CurrencyPricesEditor,
  ShopItemsEditor,
} from "../components/ShopItemsEditor";

const useStyles = createStyles((theme) => ({
  infoCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  infoLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.colors.dark[2],
  },
  infoValue: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.dark[0],
  },
}));

// ── Queries & Mutations ──

const SHOPS_QUERY = gql`
  query ($cursor: String, $count: Float) {
    shops(page: { first: $count, after: $cursor }) {
      nodes {
        id
        name
        description
        externalId
        createdAt
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const DELETE_SHOP_MUTATION = gql`
  mutation ($id: ID!) {
    shopDelete(id: $id) {
      id
    }
  }
`;

const CREATE_SHOP_MUTATION = gql`
  mutation ($input: ShopCreateInput!) {
    shopCreate(input: $input) {
      id
      name
      description
      externalId
    }
  }
`;

const UPDATE_SHOP_MUTATION = gql`
  mutation ($id: ID!, $input: ShopUpdateInput!) {
    shopUpdate(id: $id, input: $input) {
      id
      name
      description
      externalId
    }
  }
`;

const SHOP_PRODUCTS_QUERY = gql`
  query ($shopId: ID!, $cursor: String, $count: Float) {
    shopProducts(shopId: $shopId, page: { first: $count, after: $cursor }) {
      nodes {
        id
        name
        description
        price
        currency
        categoryId
        externalId
        visibleAt
        createdAt
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const SHOP_CATEGORIES_FOR_PRODUCT_QUERY = gql`
  query ($shopId: ID!, $cursor: String, $count: Float) {
    shopCategories(
      filter: { shopIds: [$shopId] }
      page: { first: $count, after: $cursor }
    ) {
      nodes {
        id
        name
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const DELETE_SHOP_PRODUCT_MUTATION = gql`
  mutation ($id: ID!) {
    shopProductDelete(productId: $id) {
      id
    }
  }
`;

const CREATE_SHOP_PRODUCT_MUTATION = gql`
  mutation ($shopId: ID!, $input: ShopProductCreateInput!) {
    shopProductCreate(shopId: $shopId, input: $input) {
      id
      name
      description
      price
      currency
      categoryId
      externalId
      visibleAt
    }
  }
`;

const UPDATE_SHOP_PRODUCT_MUTATION = gql`
  mutation ($id: ID!, $input: ShopProductUpdateInput!) {
    shopProductUpdate(productId: $id, input: $input) {
      id
      name
      description
      price
      currency
      categoryId
      externalId
      visibleAt
    }
  }
`;

const SHOPS_FOR_DROPDOWN_QUERY = gql`
  query {
    shops(page: { first: 1000 }) {
      nodes {
        id
        name
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const DELETE_SHOP_CATEGORY_MUTATION = gql`
  mutation ($id: ID!) {
    shopCategoryDelete(id: $id) {
      id
    }
  }
`;

const CREATE_SHOP_CATEGORY_MUTATION = gql`
  mutation ($input: ShopCategoryCreateInput!) {
    shopCategoryCreate(input: $input) {
      id
      name
      description
      shopId
      externalId
    }
  }
`;

const UPDATE_SHOP_CATEGORY_MUTATION = gql`
  mutation ($id: ID!, $input: ShopCategoryUpdateInput!) {
    shopCategoryUpdate(id: $id, input: $input) {
      id
      name
      description
      shopId
      externalId
    }
  }
`;

// ── Shop Product Configuration Editor ──

interface ProductConfiguration {
  maxQuantity: number | null;
  maxBoughtQuantity: number | null;
  minQuantity: number | null;
}

interface ProductConfigurationEditorProps {
  initialValue: ProductConfiguration;
  onChange: (value: ProductConfiguration) => void;
}

/**
 * Controlled editor for shop product quantity limits.
 *
 * Extracted as a component (not inlined into a DYNAMIC `element`) so that
 * `useState` / `useEffect` run at a stable position in the tree — inlining
 * hooks inside a DYNAMIC's `element(onChange) => JSX` callback violates the
 * Rules of Hooks because the callback is invoked during a parent render.
 */
function ProductConfigurationEditor({
  initialValue,
  onChange,
}: ProductConfigurationEditorProps) {
  const [config, setConfig] = useState<ProductConfiguration>(initialValue);

  useEffect(() => {
    onChange(config);
    // onChange identity is not stable from react-hook-form; intentional single-dep sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const parseOptionalInt = (raw: string): number | null => {
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  return (
    <Stack>
      <TextInput
        label="Maximum Quantity (optional)"
        type="number"
        placeholder="Leave empty for unlimited"
        value={config.maxQuantity ?? ""}
        onChange={(e) =>
          setConfig({
            ...config,
            maxQuantity: parseOptionalInt(e.target.value),
          })
        }
      />
      <TextInput
        label="Maximum Bought Quantity per Player (optional)"
        type="number"
        placeholder="Leave empty for unlimited"
        value={config.maxBoughtQuantity ?? ""}
        onChange={(e) =>
          setConfig({
            ...config,
            maxBoughtQuantity: parseOptionalInt(e.target.value),
          })
        }
      />
      <TextInput
        label="Minimum Quantity (optional)"
        type="number"
        placeholder="Leave empty for no minimum"
        value={config.minQuantity ?? ""}
        onChange={(e) =>
          setConfig({
            ...config,
            minQuantity: parseOptionalInt(e.target.value),
          })
        }
      />
    </Stack>
  );
}

const EMPTY_PRODUCT_CONFIG: ProductConfiguration = {
  maxQuantity: null,
  maxBoughtQuantity: null,
  minQuantity: null,
};

// ── Shop Category Forms ──

export function CreateShopCategoryForm({
  shopId,
  onSuccess,
}: {
  shopId?: string;
  onSuccess?: () => void;
}) {
  const [createShopCategory] = useMutation<any>(CREATE_SHOP_CATEGORY_MUTATION, {
    refetchQueries: ["shopCategories"],
  });
  const { data: shopsData } = useQuery<any>(SHOPS_FOR_DROPDOWN_QUERY);

  const shopOptions =
    shopsData?.shops?.nodes?.map((shop: any) => ({
      value: shop.id,
      label: shop.name,
    })) || [];

  return (
    <CreationForm
      title="Create Shop Category"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter category name",
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID",
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this category is for",
        },
        ...(!shopId && {
          shopId: {
            type: "SELECT" as const,
            label: "Shop",
            yupConfig: yup.string().required(),
            options: shopOptions,
            placeholder: "Select a shop",
          },
        }),
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor onChange={onChange} value={[]} />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        await createShopCategory({
          variables: {
            input: {
              name: values.name,
              description: values.description,
              externalId: values.externalId || undefined,
              shopId: shopId || values.shopId,
              metadata: values.metadata || [],
            },
          },
        });
        notifications.show({
          title: "Shop category created",
          message: "The shop category has been created successfully",
          color: "green",
          autoClose: 3000,
        });
        onSuccess?.();
      }}
    />
  );
}

export function EditShopCategoryForm({
  categoryId,
  onClose,
}: {
  categoryId: string;
  onClose: () => void;
}) {
  const [updateShopCategory] = useMutation<any>(UPDATE_SHOP_CATEGORY_MUTATION, {
    refetchQueries: ["shopCategories"],
  });
  const { data: categoryData, loading: categoryLoading } = useQuery<any>(
    gql`
      query ($id: ID!) {
        shopCategories(filter: { ids: [$id] }, page: { first: 1 }) {
          nodes {
            id
            name
            description
            shopId
            externalId
            metadata {
              property
              value
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `,
    {
      variables: { id: categoryId },
      skip: !categoryId,
    },
  );

  const { data: shopsData } = useQuery<any>(SHOPS_FOR_DROPDOWN_QUERY);

  const shopOptions =
    shopsData?.shops?.nodes?.map((shop: any) => ({
      value: shop.id,
      label: shop.name,
    })) || [];

  if (categoryLoading) return <div>Loading...</div>;

  const category = categoryData?.shopCategories?.nodes?.[0];

  return (
    <CreationForm
      title="Edit Shop Category"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter category name",
          defaultValue: category?.name,
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID",
          defaultValue: category?.externalId,
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this category is for",
          defaultValue: category?.description,
        },
        shopId: {
          type: "SELECT",
          label: "Shop",
          yupConfig: yup.string().required(),
          options: shopOptions,
          defaultValue: category?.shopId,
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: category?.metadata || [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor
              onChange={onChange}
              value={category?.metadata || []}
            />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        await updateShopCategory({
          variables: {
            id: categoryId,
            input: {
              name: values.name,
              description: values.description,
              externalId: values.externalId || undefined,
              metadata: values.metadata || [],
            },
          },
        });
        notifications.show({
          title: "Shop category updated",
          message: "The shop category has been updated successfully",
          color: "green",
          autoClose: 3000,
        });
        onClose();
      }}
    />
  );
}

// ── Shop Forms ──

export function CreateShopForm({ onSuccess }: { onSuccess?: () => void }) {
  const [createShop] = useMutation<any>(CREATE_SHOP_MUTATION, {
    refetchQueries: ["shops"],
  });

  return (
    <CreationForm
      title="Create Shop"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter shop name",
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID (optional)",
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this shop offers",
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor onChange={onChange} value={[]} />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        await createShop({
          variables: {
            input: {
              name: values.name,
              description: values.description,
              externalId: values.externalId || undefined,
              metadata: values.metadata || [],
            },
          },
        });
        notifications.show({
          title: "Shop created",
          message: "The shop has been created successfully",
          color: "green",
          autoClose: 3000,
        });
        onSuccess?.();
      }}
    />
  );
}

export function EditShopForm({
  shopId,
  onClose,
}: {
  shopId: string;
  onClose: () => void;
}) {
  const [updateShop] = useMutation<any>(UPDATE_SHOP_MUTATION, {
    refetchQueries: ["shops"],
  });
  const { data: shopData, loading: shopLoading } = useQuery<any>(
    gql`
      query ($id: ID!) {
        shops(filter: { ids: [$id] }, page: { first: 1 }) {
          nodes {
            id
            name
            description
            externalId
            metadata {
              property
              value
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `,
    {
      variables: { id: shopId },
      skip: !shopId,
    },
  );

  if (shopLoading) return <div>Loading...</div>;

  const shop = shopData?.shops?.nodes?.[0];

  return (
    <CreationForm
      title="Edit Shop"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter shop name",
          defaultValue: shop?.name,
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID (optional)",
          defaultValue: shop?.externalId,
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this shop offers",
          defaultValue: shop?.description,
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: shop?.metadata || [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor onChange={onChange} value={shop?.metadata || []} />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        await updateShop({
          variables: {
            id: shopId,
            input: {
              name: values.name,
              description: values.description,
              externalId: values.externalId || undefined,
              metadata: values.metadata || [],
            },
          },
        });
        notifications.show({
          title: "Shop updated",
          message: "The shop has been updated successfully",
          color: "green",
          autoClose: 3000,
        });
        onClose();
      }}
    />
  );
}

// ── Shop List ──

export function ShopsList() {
  return (
    <AutoDataTable
      itemPath="../shops"
      createButtonText={false}
      query={SHOPS_QUERY}
      deleteMutation={DELETE_SHOP_MUTATION}
      columns={[
        {
          accessor: "name",
          title: "Name",
        },
        {
          accessor: "externalId",
          title: "External ID",
          rawElement: (record: any) => (
            <Text size="sm" c="dimmed">
              {record.externalId || "-"}
            </Text>
          ),
        },
        {
          accessor: "description",
          title: "Description",
        },
      ]}
    />
  );
}

// ── Shop Product Forms ──

export function CreateShopProductForm({
  shopId,
  onSuccess,
}: {
  shopId: string;
  onSuccess?: () => void;
}) {
  const [createShopProduct] = useMutation<any>(CREATE_SHOP_PRODUCT_MUTATION, {
    refetchQueries: ["shopProducts"],
  });
  const { data: categoriesData } = useQuery<any>(
    SHOP_CATEGORIES_FOR_PRODUCT_QUERY,
    {
      variables: { shopId, count: 1000 },
    },
  );

  const categoryOptions =
    categoriesData?.shopCategories?.nodes?.map((category: any) => ({
      value: category.id,
      label: category.name,
    })) || [];

  const currencyOptions = [
    { value: "EUR", label: "Euro" },
    { value: "GBP", label: "British Pound" },
    { value: "USD", label: "US Dollar" },
  ];

  return (
    <CreationForm
      title="Create Shop Product"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter product name",
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID (optional)",
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this product contains",
        },
        price: {
          type: "INPUT",
          label: "Price",
          yupConfig: yup.number().min(0).optional().nullable(),
          inputType: "number",
          placeholder: "Enter price",
          defaultValue: null,
        },
        currency: {
          type: "SELECT",
          label: "Currency",
          yupConfig: yup.string().optional().nullable(),
          options: currencyOptions,
          defaultValue: null,
        },
        categoryId: {
          type: "SELECT",
          label: "Category",
          yupConfig: yup.string().optional().nullable(),
          options: [{ label: "No Category", value: "" }, ...categoryOptions],
          defaultValue: null,
        },
        visibleAt: {
          type: "INPUT",
          label: "Visible At",
          yupConfig: yup.date().optional().nullable(),
          inputType: "date",
        },
        configuration: {
          type: "DYNAMIC",
          label: "Configuration",
          yupConfig: yup.object().shape({
            maxQuantity: yup.number().integer().min(0).optional().nullable(),
            maxBoughtQuantity: yup
              .number()
              .integer()
              .min(0)
              .optional()
              .nullable(),
            minQuantity: yup.number().integer().min(0).optional().nullable(),
          }),
          defaultValue: EMPTY_PRODUCT_CONFIG,
          element: (onChange: (value: ProductConfiguration) => void) => (
            <ProductConfigurationEditor
              initialValue={EMPTY_PRODUCT_CONFIG}
              onChange={onChange}
            />
          ),
        },
        items: {
          type: "DYNAMIC",
          label: "Reward Items/Currencies",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().required(),
                quantity: yup.number().integer().min(1).required(),
                type: yup.string().oneOf(["ITEM", "CURRENCY"]).required(),
              }),
            )
            .required(),
          defaultValue: [],
          element: (onChange: (value: any) => void) => (
            <ShopItemsEditor onChange={onChange} value={[]} />
          ),
        },
        currencyPrices: {
          type: "DYNAMIC",
          label: "Alternative Currency Prices",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().required(),
                quantity: yup.number().integer().min(0).required(),
              }),
            )
            .required(),
          defaultValue: [],
          element: (onChange: (value: any) => void) => (
            <CurrencyPricesEditor onChange={onChange} value={[]} />
          ),
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor onChange={onChange} value={[]} />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        const input = {
          name: values.name,
          description: values.description,
          price: values.price,
          currency: values.currency,
          categoryId: values.categoryId || undefined,
          externalId: values.externalId || undefined,
          visibleAt: values.visibleAt || undefined,
          configuration: values.configuration,
          items: values.items || [],
          currencyPrices: values.currencyPrices || [],
          metadata: values.metadata || [],
        };

        await createShopProduct({
          variables: { shopId, input },
        });
        notifications.show({
          title: "Shop product created",
          message: "The shop product has been created successfully",
          color: "green",
          autoClose: 3000,
        });
        onSuccess?.();
      }}
    />
  );
}

export function EditShopProductForm({
  productId,
  shopId,
  onClose,
}: {
  productId: string;
  shopId: string;
  onClose: () => void;
}) {
  const [updateShopProduct] = useMutation<any>(UPDATE_SHOP_PRODUCT_MUTATION, {
    refetchQueries: ["shopProducts"],
  });
  const { data: productData, loading: productLoading } = useQuery<any>(
    gql`
      query ($shopId: ID!, $id: ID!) {
        shopProducts(
          shopId: $shopId
          filter: { ids: [$id] }
          page: { first: 1 }
        ) {
          nodes {
            id
            name
            description
            price
            currency
            categoryId
            externalId
            visibleAt
            configuration {
              maxQuantity
              maxBoughtQuantity
              minQuantity
            }
            items {
              id
              quantity
              type
            }
            currencyPrices {
              id
              quantity
            }
            metadata {
              property
              value
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `,
    {
      variables: { shopId, id: productId },
      skip: !productId,
    },
  );

  const { data: categoriesData } = useQuery<any>(
    SHOP_CATEGORIES_FOR_PRODUCT_QUERY,
    {
      variables: { shopId, count: 1000 },
    },
  );

  const categoryOptions =
    categoriesData?.shopCategories?.nodes?.map((category: any) => ({
      value: category.id,
      label: category.name,
    })) || [];

  const currencyOptions = [
    { value: "EUR", label: "Euro" },
    { value: "GBP", label: "British Pound" },
    { value: "USD", label: "US Dollar" },
  ];

  if (productLoading) return <div>Loading...</div>;

  const product = productData?.shopProducts?.nodes?.[0];

  return (
    <CreationForm
      title="Edit Shop Product"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter product name",
          defaultValue: product?.name,
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID (optional)",
          defaultValue: product?.externalId,
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this product contains",
          defaultValue: product?.description,
        },
        price: {
          type: "INPUT",
          label: "Price",
          yupConfig: yup.number().min(0).optional().nullable(),
          inputType: "number",
          placeholder: "Enter price",
          defaultValue: product?.price,
        },
        currency: {
          type: "SELECT",
          label: "Currency",
          yupConfig: yup.string().optional().nullable(),
          options: currencyOptions,
          defaultValue: product?.currency,
        },
        categoryId: {
          type: "SELECT",
          label: "Category",
          yupConfig: yup.string().optional().nullable(),
          options: [{ label: "No Category", value: "" }, ...categoryOptions],
          defaultValue: product?.categoryId,
        },
        visibleAt: {
          type: "INPUT",
          label: "Visible At",
          yupConfig: yup.date().optional().nullable(),
          inputType: "date",
          defaultValue: product?.visibleAt
            ? new Date(product.visibleAt).toISOString().slice(0, 16)
            : undefined,
        },
        configuration: {
          type: "DYNAMIC",
          label: "Configuration",
          yupConfig: yup.object().shape({
            maxQuantity: yup.number().integer().min(0).optional().nullable(),
            maxBoughtQuantity: yup
              .number()
              .integer()
              .min(0)
              .optional()
              .nullable(),
            minQuantity: yup.number().integer().min(0).optional().nullable(),
          }),
          defaultValue: product?.configuration || EMPTY_PRODUCT_CONFIG,
          element: (onChange: (value: ProductConfiguration) => void) => (
            <ProductConfigurationEditor
              initialValue={product?.configuration ?? EMPTY_PRODUCT_CONFIG}
              onChange={onChange}
            />
          ),
        },
        items: {
          type: "DYNAMIC",
          label: "Reward Items/Currencies",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().required(),
                quantity: yup.number().integer().min(1).required(),
                type: yup.string().oneOf(["ITEM", "CURRENCY"]).required(),
              }),
            )
            .required(),
          defaultValue: product?.items || [],
          element: (onChange: (value: any) => void) => (
            <ShopItemsEditor onChange={onChange} value={product?.items || []} />
          ),
        },
        currencyPrices: {
          type: "DYNAMIC",
          label: "Alternative Currency Prices",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().required(),
                quantity: yup.number().integer().min(0).required(),
              }),
            )
            .required(),
          defaultValue: product?.currencyPrices || [],
          element: (onChange: (value: any) => void) => (
            <CurrencyPricesEditor
              onChange={onChange}
              value={product?.currencyPrices || []}
            />
          ),
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: product?.metadata || [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor
              onChange={onChange}
              value={product?.metadata || []}
            />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        const input = {
          name: values.name,
          description: values.description,
          price: values.price,
          currency: values.currency,
          categoryId: values.categoryId || undefined,
          externalId: values.externalId || undefined,
          visibleAt: values.visibleAt || undefined,
          configuration: values.configuration,
          items: values.items || [],
          currencyPrices: values.currencyPrices || [],
          metadata: values.metadata || [],
        };

        await updateShopProduct({
          variables: { id: productId, input },
        });
        notifications.show({
          title: "Shop product updated",
          message: "The shop product has been updated successfully",
          color: "green",
          autoClose: 3000,
        });
        onClose();
      }}
    />
  );
}

// ── Shop Products List ──

export function ShopProductsList({ shopId }: { shopId: string }) {
  const { data: categoriesData } = useQuery<any>(
    SHOP_CATEGORIES_FOR_PRODUCT_QUERY,
    {
      variables: { shopId, count: 1000 },
    },
  );
  const categoryMap = new Map<string, string>(
    categoriesData?.shopCategories?.nodes?.map((c: any) => [c.id, c.name]) ||
      [],
  );

  const handleEditClick = (item: any) => {
    const modalId = `edit-shop-product-${item.id}`;
    modals.open({
      modalId,
      title: "Edit Shop Product",
      size: "lg",
      children: (
        <EditShopProductForm
          productId={item.id}
          shopId={shopId}
          onClose={() => modals.close(modalId)}
        />
      ),
    });
  };

  const formatPrice = (record: any) => {
    if (record.price == null) return "-";
    const formatted =
      typeof record.price === "number" ? record.price.toFixed(2) : record.price;
    return record.currency ? `${formatted} ${record.currency}` : formatted;
  };

  return (
    <AutoDataTable
      itemPath="../products"
      createButtonText={false}
      query={SHOP_PRODUCTS_QUERY}
      queryVariables={{ shopId }}
      deleteMutation={DELETE_SHOP_PRODUCT_MUTATION}
      linkClick={handleEditClick}
      columns={[
        {
          accessor: "name",
          title: "Name",
        },
        {
          accessor: "externalId",
          title: "External ID",
          rawElement: (record: any) => (
            <Text size="sm" c="dimmed">
              {record.externalId || "-"}
            </Text>
          ),
        },
        {
          accessor: "price",
          title: "Price",
          rawElement: (record: any) => (
            <Text size="sm">{formatPrice(record)}</Text>
          ),
        },
        {
          accessor: "categoryId",
          title: "Category",
          rawElement: (record: any) => (
            <Text size="sm">{categoryMap.get(record.categoryId) || "-"}</Text>
          ),
        },
        {
          accessor: "description",
          title: "Description",
        },
      ]}
    />
  );
}

// ── Shop Dashboard ──

export function ShopDashboard() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { shopId } = useParams();
  const { confirm } = useConfirmModal();
  const [deleteShop] = useMutation<any>(DELETE_SHOP_MUTATION, {
    refetchQueries: ["shops"],
  });

  const {
    data: shopData,
    loading: shopLoading,
    refetch,
  } = useQuery<any>(
    gql`
      query ($id: ID!) {
        shops(filter: { ids: [$id] }, page: { first: 1 }) {
          nodes {
            id
            name
            description
            externalId
            metadata {
              property
              value
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `,
    {
      variables: { id: shopId },
      skip: !shopId,
    },
  );

  const { data: productsData } = useQuery<any>(SHOP_PRODUCTS_QUERY, {
    variables: { shopId, count: 1 },
    skip: !shopId,
  });

  const { data: categoriesData } = useQuery<any>(
    SHOP_CATEGORIES_FOR_PRODUCT_QUERY,
    {
      variables: { shopId, count: 1 },
      skip: !shopId,
    },
  );

  if (shopLoading) return <DetailPageSkeleton />;
  if (!shopId) return <div>Shop ID is required</div>;

  const shop = shopData?.shops?.nodes?.[0];
  const productsCount = productsData?.shopProducts?.totalCount ?? 0;
  const categoriesCount = categoriesData?.shopCategories?.totalCount ?? 0;

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Delete Shop",
      message: `Are you sure you want to delete "${shop?.name}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      type: "danger",
    });
    if (confirmed) {
      await deleteShop({ variables: { id: shopId } });
      notifications.show({
        title: "Shop deleted",
        message: "The shop has been deleted successfully",
        color: "green",
        autoClose: 3000,
      });
      navigate("/gamification/shops");
    }
  };

  const openEdit = () => {
    if (!shopId) return;
    const modalId = `edit-shop-${shopId}`;
    modals.open({
      modalId,
      title: "Edit Shop",
      size: "lg",
      children: (
        <EditShopForm shopId={shopId} onClose={() => modals.close(modalId)} />
      ),
    });
  };

  return (
    <div className="wp-fade-in">
      <DetailPageHeader
        title={shop?.name ?? "Shop"}
        subtitle={shop?.description}
        onEdit={openEdit}
        onDelete={handleDelete}
        onRefresh={() => refetch()}
        showBreadcrumbs={false}
      />

      {/* Stat Cards */}
      <SimpleGrid
        cols={{ base: 1, md: 3 }}
        className="wp-stagger-children"
        mb="lg"
      >
        <StatCard
          label="Products"
          value={productsCount}
          icon={<IconPackage size={16} stroke={1.5} />}
          description="Total products in shop"
          onClick={() => navigate("products")}
          tooltip="Click to manage products"
        />
        <StatCard
          label="Categories"
          value={categoriesCount}
          icon={<IconCategory size={16} stroke={1.5} />}
          description="Product categories"
          onClick={() => navigate("categories")}
          tooltip="Click to manage categories"
        />
        <StatCard
          label="External ID"
          value={shop?.externalId || "-"}
          iconType="stats"
          description="External system reference"
        />
      </SimpleGrid>

      {/* Shop Metadata */}
      {shop?.metadata && shop.metadata.length > 0 && (
        <Card className={classes.infoCard} p="md" radius="lg" mb="lg">
          <Text fw={600} mb="md">
            Metadata
          </Text>
          <SimpleGrid cols={2} spacing="md">
            {shop.metadata.map((m: any) => (
              <Stack key={m.property} gap={4}>
                <Text className={classes.infoLabel}>{m.property}</Text>
                <Text className={classes.infoValue}>{m.value}</Text>
              </Stack>
            ))}
          </SimpleGrid>
        </Card>
      )}

      {/* Quick Actions */}
      <Group mb="lg">
        <Button
          leftSection={<IconPackage size={16} />}
          variant="light"
          onClick={() => navigate("products")}
          className="wp-btn-press"
        >
          Manage Products
        </Button>
        <Button
          leftSection={<IconCategory size={16} />}
          variant="light"
          onClick={() => navigate("categories")}
          className="wp-btn-press"
        >
          Manage Categories
        </Button>
        <Button
          leftSection={<IconShoppingCart size={16} />}
          variant="light"
          onClick={() => navigate("purchases")}
          className="wp-btn-press"
        >
          Purchase History
        </Button>
      </Group>
    </div>
  );
}

// ── Shop Categories List ──

export function ShopCategoriesList({ shopId }: { shopId: string }) {
  const handleEditClick = (item: any) => {
    const modalId = `edit-shop-category-${item.id}`;
    modals.open({
      modalId,
      title: "Edit Shop Category",
      size: "lg",
      children: (
        <EditShopCategoryForm
          categoryId={item.id}
          onClose={() => modals.close(modalId)}
        />
      ),
    });
  };

  return (
    <AutoDataTable
      itemPath="../categories"
      createButtonText={false}
      query={SHOP_CATEGORIES_FOR_PRODUCT_QUERY}
      queryVariables={{ shopId }}
      deleteMutation={DELETE_SHOP_CATEGORY_MUTATION}
      linkClick={handleEditClick}
      columns={[
        {
          accessor: "name",
          title: "Name",
        },
      ]}
    />
  );
}

// ── Page Components ──

const openCreateShopModal = () => {
  const modalId = "create-shop";
  modals.open({
    modalId,
    title: "Create Shop",
    size: "lg",
    children: <CreateShopForm onSuccess={() => modals.close(modalId)} />,
  });
};

export function ShopsHome() {
  const { getLimit } = useOrganizationLimits(["SHOPS"]);
  const shopsLimit = getLimit("SHOPS");

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Shops"
        subtitle="Manage shops where players can purchase virtual items and products"
        createLabel="Create Shop"
        onCreate={openCreateShopModal}
        showBreadcrumbs={false}
      />
      {shopsLimit && (
        <Group mb="md">
          <LimitBadge limit={shopsLimit} label="Shops limit" />
        </Group>
      )}
      <ShopsList />
    </div>
  );
}

export function ShopsCategories() {
  const { shopId } = useParams<{ shopId: string }>();

  if (!shopId) return null;

  const openCreate = () => {
    const modalId = `create-shop-category-${shopId}`;
    modals.open({
      modalId,
      title: "Create Shop Category",
      size: "lg",
      children: (
        <CreateShopCategoryForm
          shopId={shopId}
          onSuccess={() => modals.close(modalId)}
        />
      ),
    });
  };

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Shop Categories"
        subtitle="Organize products within this shop for better navigation"
        createLabel="Create Category"
        onCreate={openCreate}
        showBreadcrumbs={false}
      />
      <ShopCategoriesList shopId={shopId} />
    </div>
  );
}

export function ShopsProducts() {
  const { shopId } = useParams<{ shopId: string }>();

  if (!shopId) return null;

  const openCreate = () => {
    const modalId = `create-shop-product-${shopId}`;
    modals.open({
      modalId,
      title: "Create Shop Product",
      size: "lg",
      children: (
        <CreateShopProductForm
          shopId={shopId}
          onSuccess={() => modals.close(modalId)}
        />
      ),
    });
  };

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Shop Products"
        subtitle="Items players can purchase in this shop"
        createLabel="Create Product"
        onCreate={openCreate}
        showBreadcrumbs={false}
      />
      <ShopProductsList shopId={shopId} />
    </div>
  );
}
